/**
 * Recurring Schedule Modal Component
 * 
 * Modal for creating and managing recurring invoice/expense schedules.
 * Allows setting frequency, dates, auto-send options, etc.
 * 
 * @version 1.0.0
 * @date January 2026
 */

'use client';

import React, { useState, useEffect, useCallback } from 'react';
import {
  X, Calendar, Clock, Repeat, Send, Save, AlertCircle,
  ChevronDown, CheckCircle, Pause, Play, Trash2, Loader2,
  Info, Mail, DollarSign, FileText, User, Building, Plus, Search
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { authService } from '@/lib/authService';
import { notify } from '@/lib/notifications';
import type { Document, DocumentType, DocumentLineItem } from '@/hooks/useDocumentsWorkItems';

// ============================================================================
// TYPES
// ============================================================================

export interface RecurringSchedule {
  id?: string;
  client_id?: number;
  name: string;
  description?: string;
  document_type: DocumentType;
  
  // Contact
  contact_id?: number;
  contact_name?: string;
  contact_email?: string;
  billing_address?: string;
  shipping_address?: string;
  
  // Financial
  currency?: string;
  payment_terms?: number;
  discount_percentage?: number;
  tax_rate_id?: number;
  
  // Line items
  line_items: DocumentLineItem[];
  
  // Content
  notes?: string;
  terms_and_conditions?: string;
  internal_notes?: string;
  reference?: string;
  
  // Schedule
  frequency: 'daily' | 'weekly' | 'fortnightly' | 'monthly' | 'quarterly' | 'biannually' | 'annually';
  interval_count: number;
  day_of_week?: number;
  day_of_month?: number;
  month_of_year?: number;
  generate_time?: string;
  timezone?: string;
  
  // Dates
  start_date: string;
  end_date?: string;
  end_after_occurrences?: number;
  
  // Status
  is_active?: boolean;
  is_paused?: boolean;
  
  // Auto-send
  auto_send?: boolean;
  auto_approve?: boolean;
  send_days_before_due?: number;
  cc_emails?: string[];
  
  // Due date
  due_days_after_issue?: number;
  
  // Tracking
  next_generation_date?: string;
  last_generated_at?: string;
  total_generated?: number;
  total_amount_generated?: number;
  
  // Source document
  source_document_id?: string;
  source_document_type?: string;
}

interface RecurringScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  document?: Document | null; // Source document to create recurring from
  existingSchedule?: RecurringSchedule | null; // Edit existing schedule
  onSaved?: (schedule: RecurringSchedule) => void;
}

interface FinanceContact {
  id: number;
  company_name?: string | null;
  display_name?: string | null;
  contact_person?: string | null;
  email?: string | null;
  billing_address?: string | null;
  shipping_address?: string | null;
  physical_address?: string | null;
  postal_address?: string | null;
  contact_type?: string | null;
}

// ============================================================================
// FREQUENCY OPTIONS
// ============================================================================

const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily', description: 'Every day' },
  { value: 'weekly', label: 'Weekly', description: 'Every week' },
  { value: 'fortnightly', label: 'Fortnightly', description: 'Every 2 weeks' },
  { value: 'monthly', label: 'Monthly', description: 'Every month' },
  { value: 'quarterly', label: 'Quarterly', description: 'Every 3 months' },
  { value: 'biannually', label: 'Bi-annually', description: 'Every 6 months' },
  { value: 'annually', label: 'Annually', description: 'Once a year' },
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const STANDALONE_DOCUMENT_TYPE_OPTIONS: Array<{ value: DocumentType; label: string }> = [
  { value: 'invoice', label: 'Invoice' },
  { value: 'expense', label: 'Expense' },
];

const createEmptyLineItem = (): DocumentLineItem => ({
  description: '',
  quantity: 1,
  unit_price: 0,
  tax_rate: 0,
  tax_amount: 0,
});

// ============================================================================
// COMPONENT
// ============================================================================

export default function RecurringScheduleModal({
  isOpen,
  onClose,
  document,
  existingSchedule,
  onSaved
}: RecurringScheduleModalProps) {
  const { currentTheme, clientBranding } = useTheme();
  const isDark = currentTheme?.mode === 'dark';
  const primaryColor = clientBranding?.primary_color || '#3B82F6';
  const hasSourceDocument = Boolean(document?.id || existingSchedule?.source_document_id);
  
  // Form state
  const [formData, setFormData] = useState<Partial<RecurringSchedule>>({
    name: '',
    description: '',
    document_type: 'invoice',
    frequency: 'monthly',
    interval_count: 1,
    day_of_month: 1,
    start_date: new Date().toISOString().split('T')[0],
    is_active: true,
    auto_send: false,
    auto_approve: false,
    due_days_after_issue: 30,
    payment_terms: 30,
    line_items: [],
    currency: 'ZAR',
    timezone: 'Africa/Johannesburg',
    generate_time: '06:00',
  });
  
  const [saving, setSaving] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [ccEmailInput, setCcEmailInput] = useState('');
  const [contacts, setContacts] = useState<FinanceContact[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [contactSearch, setContactSearch] = useState('');

  const loadContacts = useCallback(async () => {
    try {
      setLoadingContacts(true);
      const token = authService.getTokens()?.access_token;
      if (!token) return;

      const response = await fetch('/api/v1/contacts?active=true&limit=500', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();
      if (response.ok && result.success) {
        setContacts(result.data || []);
      }
    } catch (error) {
      console.error('Load contacts error:', error);
    } finally {
      setLoadingContacts(false);
    }
  }, []);
  
  // Initialize from document or existing schedule
  useEffect(() => {
    if (existingSchedule) {
      setFormData({
        ...existingSchedule,
        start_date: existingSchedule.start_date || new Date().toISOString().split('T')[0],
        generate_time: existingSchedule.generate_time?.slice(0, 5) || '06:00',
        line_items: existingSchedule.line_items?.length ? existingSchedule.line_items : [createEmptyLineItem()],
      });
    } else if (document) {
      // Create schedule from existing document
      setFormData(prev => ({
        ...prev,
        name: `Recurring ${document.doc_type === 'invoice' ? 'Invoice' : document.doc_type === 'expense' ? 'Expense' : 'Document'} - ${document.contact_name || 'Customer'}`,
        document_type: document.doc_type as DocumentType,
        contact_id: document.contact_id,
        contact_name: document.contact_name,
        contact_email: document.contact_email,
        billing_address: document.billing_address,
        shipping_address: document.shipping_address,
        currency: document.currency || 'ZAR',
        payment_terms: document.payment_terms || 30,
        due_days_after_issue: document.payment_terms || 30,
        notes: document.notes,
        terms_and_conditions: document.terms_and_conditions,
        reference: document.reference,
        line_items: document.items?.length ? document.items : [createEmptyLineItem()],
        source_document_id: document.id,
        source_document_type: document.doc_type,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        line_items: prev.line_items?.length ? prev.line_items : [createEmptyLineItem()],
      }));
    }
  }, [document, existingSchedule]);

  useEffect(() => {
    if (isOpen) {
      loadContacts();
    }
  }, [isOpen, loadContacts]);
  
  // Handle form field changes
  const handleChange = (field: keyof RecurringSchedule, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const filteredContacts = contacts.filter(contact => {
    if (!contactSearch.trim()) return true;
    const query = contactSearch.toLowerCase();
    const haystack = `${contact.display_name || ''} ${contact.company_name || ''} ${contact.contact_person || ''} ${contact.email || ''}`.toLowerCase();
    return haystack.includes(query);
  });

  const handleSelectContact = (contactId: string) => {
    if (!contactId) {
      setFormData(prev => ({
        ...prev,
        contact_id: undefined,
        contact_name: undefined,
        contact_email: undefined,
        billing_address: undefined,
        shipping_address: undefined,
      }));
      return;
    }

    const selectedContact = contacts.find(contact => String(contact.id) === contactId);
    if (!selectedContact) return;

    const resolvedName = selectedContact.company_name || selectedContact.display_name || selectedContact.contact_person || `Contact #${selectedContact.id}`;
    const resolvedBillingAddress = selectedContact.billing_address || selectedContact.postal_address || selectedContact.physical_address || '';
    const resolvedShippingAddress = selectedContact.shipping_address || selectedContact.physical_address || selectedContact.postal_address || '';

    setFormData(prev => ({
      ...prev,
      contact_id: selectedContact.id,
      contact_name: resolvedName,
      contact_email: selectedContact.email || undefined,
      billing_address: resolvedBillingAddress || undefined,
      shipping_address: resolvedShippingAddress || undefined,
    }));
  };

  const handleLineItemChange = (index: number, field: keyof DocumentLineItem, value: string | number) => {
    const updatedLineItems = [...(formData.line_items || [])];
    const currentItem = updatedLineItems[index] || createEmptyLineItem();
    const nextItem: DocumentLineItem = {
      ...currentItem,
      [field]: value,
    } as DocumentLineItem;

    const quantity = Number(nextItem.quantity || 0);
    const unitPrice = Number(nextItem.unit_price || 0);
    const taxRate = Number(nextItem.tax_rate || 0);
    const lineTotal = quantity * unitPrice;

    nextItem.line_total = lineTotal;
    nextItem.tax_amount = lineTotal * (taxRate / 100);
    updatedLineItems[index] = nextItem;

    handleChange('line_items', updatedLineItems);
  };

  const handleAddLineItem = () => {
    handleChange('line_items', [...(formData.line_items || []), createEmptyLineItem()]);
  };

  const handleRemoveLineItem = (index: number) => {
    const updatedLineItems = (formData.line_items || []).filter((_, itemIndex) => itemIndex !== index);
    handleChange('line_items', updatedLineItems.length > 0 ? updatedLineItems : [createEmptyLineItem()]);
  };
  
  // Add CC email
  const handleAddCcEmail = () => {
    if (ccEmailInput && ccEmailInput.includes('@')) {
      const emails = formData.cc_emails || [];
      if (!emails.includes(ccEmailInput)) {
        handleChange('cc_emails', [...emails, ccEmailInput]);
      }
      setCcEmailInput('');
    }
  };
  
  // Remove CC email
  const handleRemoveCcEmail = (email: string) => {
    const emails = formData.cc_emails || [];
    handleChange('cc_emails', emails.filter(e => e !== email));
  };
  
  // Calculate estimated total from line items
  const estimatedTotal = (formData.line_items || []).reduce((sum, item) => {
    const lineTotal = (item.quantity || 0) * (item.unit_price || 0);
    const taxAmount = lineTotal * ((item.tax_rate || 0) / 100);
    return sum + lineTotal + taxAmount;
  }, 0);
  
  // Save schedule
  const handleSave = async () => {
    // Validation
    if (!formData.name?.trim()) {
      notify.error('Please enter a schedule name');
      return;
    }
    if (!formData.start_date) {
      notify.error('Please select a start date');
      return;
    }
    if (!formData.contact_id && !formData.contact_name) {
      notify.error('Please select a customer/supplier');
      return;
    }
    if (!formData.line_items || formData.line_items.length === 0) {
      notify.error('Please add at least one line item');
      return;
    }
    
    setSaving(true);
    
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) throw new Error('Not authenticated');
      
      const endpoint = existingSchedule?.id 
        ? '/api/v1/finance/recurring' 
        : '/api/v1/finance/recurring';
      
      const response = await fetch(endpoint, {
        method: existingSchedule?.id ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: existingSchedule?.id ? 'update' : 'create',
          id: existingSchedule?.id,
          ...formData,
        }),
      });
      
      const result = await response.json();
      
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to save recurring schedule');
      }
      
      notify.success(existingSchedule?.id ? 'Schedule updated' : 'Recurring schedule created');
      onSaved?.(result.data);
      onClose();
      
    } catch (err: any) {
      console.error('Save recurring schedule error:', err);
      notify.error(err.message || 'Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };
  
  if (!isOpen) return null;
  
  // Get description for frequency
  const getFrequencyDescription = () => {
    const freq = FREQUENCY_OPTIONS.find(f => f.value === formData.frequency);
    let desc = freq?.description || '';
    
    if (formData.interval_count && formData.interval_count > 1) {
      desc = `Every ${formData.interval_count} ${formData.frequency === 'daily' ? 'days' : 
        formData.frequency === 'weekly' ? 'weeks' : 
        formData.frequency === 'monthly' ? 'months' : 
        formData.frequency === 'quarterly' ? 'quarters' : 
        formData.frequency === 'annually' ? 'years' : 'periods'}`;
    }
    
    if (formData.frequency === 'weekly' && formData.day_of_week !== undefined) {
      desc += ` on ${DAYS_OF_WEEK[formData.day_of_week]?.label}`;
    }
    
    if (['monthly', 'quarterly', 'biannually'].includes(formData.frequency || '') && formData.day_of_month) {
      const suffix = formData.day_of_month === 1 ? 'st' : 
        formData.day_of_month === 2 ? 'nd' : 
        formData.day_of_month === 3 ? 'rd' : 'th';
      desc += ` on the ${formData.day_of_month}${suffix}`;
    }
    
    if (formData.frequency === 'annually' && formData.month_of_year) {
      desc += ` in ${MONTHS[formData.month_of_year - 1]?.label}`;
    }
    
    return desc;
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className={`w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-xl shadow-2xl flex flex-col ${
        isDark ? 'bg-gray-800' : 'bg-white'
      }`}>
        {/* Header */}
        <div 
          className="flex items-center justify-between px-6 py-4 text-white"
          style={{ backgroundColor: primaryColor }}
        >
          <div className="flex items-center gap-3">
            <Repeat className="w-6 h-6" />
            <div>
              <h2 className="text-lg font-semibold">
                {existingSchedule?.id ? 'Edit Recurring Schedule' : 'Create Recurring Schedule'}
              </h2>
              <p className="text-sm text-white/70">
                {formData.document_type === 'invoice' ? 'Recurring Invoice' : 
                 formData.document_type === 'expense' ? 'Recurring Expense' : 
                 'Recurring Document'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Schedule Name */}
          <div>
            <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Schedule Name *
            </label>
            <input
              type="text"
              value={formData.name || ''}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="e.g., Monthly Retainer - ABC Company"
              className={`w-full px-3 py-2.5 rounded-lg border ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
              } focus:outline-none focus:ring-2 focus:ring-blue-500/30`}
            />
          </div>
          
          {/* Standalone Document Setup */}
          {!hasSourceDocument && (
            <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-700/30 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
              <h3 className={`font-medium mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                <FileText className="w-5 h-5" />
                Document Template
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Document Type
                  </label>
                  <select
                    value={formData.document_type || 'invoice'}
                    onChange={(e) => handleChange('document_type', e.target.value as DocumentType)}
                    className={`w-full px-3 py-2.5 rounded-lg border ${
                      isDark
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500/30`}
                  >
                    {STANDALONE_DOCUMENT_TYPE_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Find Contact
                  </label>
                  <div className="relative">
                    <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                    <input
                      type="text"
                      value={contactSearch}
                      onChange={(e) => setContactSearch(e.target.value)}
                      placeholder="Search name or email"
                      className={`w-full pl-9 pr-3 py-2.5 rounded-lg border ${
                        isDark
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                      } focus:outline-none focus:ring-2 focus:ring-blue-500/30`}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Customer / Supplier *
                </label>
                <select
                  value={formData.contact_id || ''}
                  onChange={(e) => handleSelectContact(e.target.value)}
                  disabled={loadingContacts}
                  className={`w-full px-3 py-2.5 rounded-lg border ${
                    isDark
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500/30 disabled:opacity-60`}
                >
                  <option value="">{loadingContacts ? 'Loading contacts...' : 'Select a contact'}</option>
                  {filteredContacts.map(contact => {
                    const label = contact.company_name || contact.display_name || contact.contact_person || `Contact #${contact.id}`;
                    return (
                      <option key={contact.id} value={contact.id}>
                        {label}{contact.email ? ` • ${contact.email}` : ''}
                      </option>
                    );
                  })}
                </select>
                {!loadingContacts && filteredContacts.length === 0 && (
                  <p className={`mt-2 text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    No contacts match your search.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Customer Info Banner */}
          {(formData.contact_name || formData.contact_id) && (
            <div className={`p-4 rounded-lg border ${
              isDark ? 'bg-gray-700/50 border-gray-600' : 'bg-blue-50 border-blue-200'
            }`}>
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${isDark ? 'bg-blue-900/50' : 'bg-blue-100'}`}>
                  {formData.document_type === 'expense' ? (
                    <Building className={`w-5 h-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                  ) : (
                    <User className={`w-5 h-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                  )}
                </div>
                <div>
                  <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {formData.contact_name}
                  </div>
                  {formData.contact_email && (
                    <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {formData.contact_email}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Frequency Section */}
          <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-700/30 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
            <h3 className={`font-medium mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <Calendar className="w-5 h-5" />
              Schedule Frequency
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              {/* Frequency */}
              <div>
                <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Repeat
                </label>
                <select
                  value={formData.frequency || 'monthly'}
                  onChange={(e) => handleChange('frequency', e.target.value)}
                  className={`w-full px-3 py-2.5 rounded-lg border ${
                    isDark 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300 text-gray-900'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500/30`}
                >
                  {FREQUENCY_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              
              {/* Interval */}
              <div>
                <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Every
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    max="12"
                    value={formData.interval_count || 1}
                    onChange={(e) => handleChange('interval_count', parseInt(e.target.value) || 1)}
                    className={`w-20 px-3 py-2.5 rounded-lg border ${
                      isDark 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500/30`}
                  />
                  <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {formData.frequency === 'daily' ? 'day(s)' : 
                     formData.frequency === 'weekly' ? 'week(s)' : 
                     formData.frequency === 'monthly' ? 'month(s)' : 
                     'period(s)'}
                  </span>
                </div>
              </div>
              
              {/* Day of Week (for weekly) */}
              {formData.frequency === 'weekly' && (
                <div>
                  <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    On Day
                  </label>
                  <select
                    value={formData.day_of_week ?? 1}
                    onChange={(e) => handleChange('day_of_week', parseInt(e.target.value))}
                    className={`w-full px-3 py-2.5 rounded-lg border ${
                      isDark 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500/30`}
                  >
                    {DAYS_OF_WEEK.map(day => (
                      <option key={day.value} value={day.value}>{day.label}</option>
                    ))}
                  </select>
                </div>
              )}
              
              {/* Day of Month (for monthly, quarterly, etc.) */}
              {['monthly', 'quarterly', 'biannually', 'annually'].includes(formData.frequency || '') && (
                <div>
                  <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Day of Month
                  </label>
                  <select
                    value={formData.day_of_month || 1}
                    onChange={(e) => handleChange('day_of_month', parseInt(e.target.value))}
                    className={`w-full px-3 py-2.5 rounded-lg border ${
                      isDark 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500/30`}
                  >
                    {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                      <option key={day} value={day}>{day}</option>
                    ))}
                    <option value={29}>29 (or last day)</option>
                    <option value={30}>30 (or last day)</option>
                    <option value={31}>Last day of month</option>
                  </select>
                </div>
              )}
              
              {/* Month of Year (for annually) */}
              {formData.frequency === 'annually' && (
                <div>
                  <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Month
                  </label>
                  <select
                    value={formData.month_of_year || 1}
                    onChange={(e) => handleChange('month_of_year', parseInt(e.target.value))}
                    className={`w-full px-3 py-2.5 rounded-lg border ${
                      isDark 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500/30`}
                  >
                    {MONTHS.map(month => (
                      <option key={month.value} value={month.value}>{month.label}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            
            {/* Schedule Summary */}
            <div className={`mt-4 p-3 rounded-lg flex items-center gap-2 ${
              isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-50 text-blue-700'
            }`}>
              <Repeat className="w-4 h-4" />
              <span className="text-sm">{getFrequencyDescription()}</span>
            </div>
          </div>
          
          {/* Date Range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Start Date *
              </label>
              <input
                type="date"
                value={formData.start_date || ''}
                onChange={(e) => handleChange('start_date', e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className={`w-full px-3 py-2.5 rounded-lg border ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                } focus:outline-none focus:ring-2 focus:ring-blue-500/30`}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                End Date (optional)
              </label>
              <input
                type="date"
                value={formData.end_date || ''}
                onChange={(e) => handleChange('end_date', e.target.value || undefined)}
                min={formData.start_date}
                className={`w-full px-3 py-2.5 rounded-lg border ${
                  isDark 
                    ? 'bg-gray-700 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                } focus:outline-none focus:ring-2 focus:ring-blue-500/30`}
              />
              <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Leave empty for indefinite
              </p>
            </div>
          </div>
          
          {/* Line Items */}
          <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-700/30 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
            <div className="flex items-center justify-between gap-3 mb-3">
              <h3 className={`font-medium flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                <FileText className="w-5 h-5" />
                Line Items ({formData.line_items?.length || 0})
              </h3>
              <button
                type="button"
                onClick={handleAddLineItem}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm flex items-center gap-1.5"
              >
                <Plus className="w-4 h-4" />
                Add Item
              </button>
            </div>

            <div className="space-y-3">
              {(formData.line_items || []).map((item, idx) => {
                const lineTotal = (item.quantity || 0) * (item.unit_price || 0);
                return (
                  <div
                    key={`${idx}-${item.id || 'new'}`}
                    className={`p-3 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                      <div className="md:col-span-5">
                        <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Description
                        </label>
                        <input
                          type="text"
                          value={item.description || item.item_name || ''}
                          onChange={(e) => handleLineItemChange(idx, 'description', e.target.value)}
                          placeholder="Monthly service retainer"
                          className={`w-full px-3 py-2 rounded-lg border ${
                            isDark
                              ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                          } focus:outline-none focus:ring-2 focus:ring-blue-500/30`}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Qty
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.quantity ?? 1}
                          onChange={(e) => handleLineItemChange(idx, 'quantity', parseFloat(e.target.value) || 0)}
                          className={`w-full px-3 py-2 rounded-lg border ${
                            isDark
                              ? 'bg-gray-700 border-gray-600 text-white'
                              : 'bg-white border-gray-300 text-gray-900'
                          } focus:outline-none focus:ring-2 focus:ring-blue-500/30`}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Unit Price
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unit_price ?? 0}
                          onChange={(e) => handleLineItemChange(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                          className={`w-full px-3 py-2 rounded-lg border ${
                            isDark
                              ? 'bg-gray-700 border-gray-600 text-white'
                              : 'bg-white border-gray-300 text-gray-900'
                          } focus:outline-none focus:ring-2 focus:ring-blue-500/30`}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          Tax %
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.tax_rate ?? 0}
                          onChange={(e) => handleLineItemChange(idx, 'tax_rate', parseFloat(e.target.value) || 0)}
                          className={`w-full px-3 py-2 rounded-lg border ${
                            isDark
                              ? 'bg-gray-700 border-gray-600 text-white'
                              : 'bg-white border-gray-300 text-gray-900'
                          } focus:outline-none focus:ring-2 focus:ring-blue-500/30`}
                        />
                      </div>
                      <div className="md:col-span-1 flex md:items-end">
                        <button
                          type="button"
                          onClick={() => handleRemoveLineItem(idx)}
                          className={`w-full md:w-auto h-10 px-3 rounded-lg transition-colors ${
                            isDark ? 'text-red-400 hover:bg-red-900/30' : 'text-red-600 hover:bg-red-50'
                          }`}
                          title="Remove line item"
                        >
                          <Trash2 className="w-4 h-4 mx-auto" />
                        </button>
                      </div>
                    </div>

                    <div className={`mt-3 text-sm flex items-center justify-between ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      <span>Tax: R {((item.tax_amount || 0)).toFixed(2)}</span>
                      <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Line total: R {lineTotal.toFixed(2)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Estimated Total */}
            <div className={`mt-4 pt-3 border-t flex items-center justify-between ${isDark ? 'border-gray-600' : 'border-gray-200'}`}>
              <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Estimated Total (per invoice)
              </span>
              <span className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                R {estimatedTotal.toFixed(2)}
              </span>
            </div>
          </div>
          
          {/* Auto-Send Options */}
          <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-700/30 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
            <h3 className={`font-medium mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <Send className="w-5 h-5" />
              Delivery Options
            </h3>
            
            <div className="space-y-4">
              {/* Auto-approve toggle */}
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Auto-approve documents
                  </div>
                  <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Set status to "Sent" instead of "Draft"
                  </div>
                </div>
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={formData.auto_approve || false}
                    onChange={(e) => handleChange('auto_approve', e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`w-11 h-6 rounded-full transition-colors ${
                    formData.auto_approve ? 'bg-blue-600' : isDark ? 'bg-gray-600' : 'bg-gray-300'
                  }`}>
                    <div className={`w-5 h-5 rounded-full bg-white shadow-sm transform transition-transform mt-0.5 ${
                      formData.auto_approve ? 'translate-x-5.5 ml-0.5' : 'translate-x-0.5'
                    }`} />
                  </div>
                </div>
              </label>
              
              {/* Auto-send toggle */}
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Auto-send by email
                  </div>
                  <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Email the document to the customer automatically
                  </div>
                </div>
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={formData.auto_send || false}
                    onChange={(e) => handleChange('auto_send', e.target.checked)}
                    className="sr-only"
                  />
                  <div className={`w-11 h-6 rounded-full transition-colors ${
                    formData.auto_send ? 'bg-green-600' : isDark ? 'bg-gray-600' : 'bg-gray-300'
                  }`}>
                    <div className={`w-5 h-5 rounded-full bg-white shadow-sm transform transition-transform mt-0.5 ${
                      formData.auto_send ? 'translate-x-5.5 ml-0.5' : 'translate-x-0.5'
                    }`} />
                  </div>
                </div>
              </label>
              
              {/* CC Emails (if auto-send enabled) */}
              {formData.auto_send && (
                <div>
                  <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    CC Recipients (optional)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={ccEmailInput}
                      onChange={(e) => setCcEmailInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddCcEmail();
                        }
                      }}
                      placeholder="email@example.com"
                      className={`flex-1 px-3 py-2 rounded-lg border ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                          : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                      } focus:outline-none focus:ring-2 focus:ring-blue-500/30`}
                    />
                    <button
                      onClick={handleAddCcEmail}
                      className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Add
                    </button>
                  </div>
                  {formData.cc_emails && formData.cc_emails.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.cc_emails.map(email => (
                        <span 
                          key={email} 
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                            isDark ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'
                          }`}
                        >
                          {email}
                          <button 
                            onClick={() => handleRemoveCcEmail(email)}
                            className="hover:text-red-500"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* Advanced Options (collapsible) */}
          <div className={`rounded-lg border ${isDark ? 'border-gray-600' : 'border-gray-200'}`}>
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className={`w-full flex items-center justify-between px-4 py-3 ${
                isDark ? 'text-gray-300 hover:bg-gray-700/50' : 'text-gray-700 hover:bg-gray-50'
              } transition-colors`}
            >
              <span className="font-medium">Advanced Options</span>
              <ChevronDown className={`w-5 h-5 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
            </button>
            
            {showAdvanced && (
              <div className={`p-4 border-t space-y-4 ${isDark ? 'border-gray-600' : 'border-gray-200'}`}>
                {/* Payment Terms */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Payment Terms (days)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.payment_terms || 30}
                      onChange={(e) => handleChange('payment_terms', parseInt(e.target.value) || 30)}
                      className={`w-full px-3 py-2 rounded-lg border ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      } focus:outline-none focus:ring-2 focus:ring-blue-500/30`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Due Days After Issue
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.due_days_after_issue || 30}
                      onChange={(e) => handleChange('due_days_after_issue', parseInt(e.target.value) || 30)}
                      className={`w-full px-3 py-2 rounded-lg border ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      } focus:outline-none focus:ring-2 focus:ring-blue-500/30`}
                    />
                  </div>
                </div>
                
                {/* End after occurrences */}
                <div>
                  <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    End After # Occurrences (optional)
                  </label>
                  <input
                    type="number"
                    min="1"
                    placeholder="Leave empty for indefinite"
                    value={formData.end_after_occurrences || ''}
                    onChange={(e) => handleChange('end_after_occurrences', e.target.value ? parseInt(e.target.value) : undefined)}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      isDark 
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' 
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500/30`}
                  />
                </div>
                
                {/* Generation Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Generate At Time
                    </label>
                    <input
                      type="time"
                      value={formData.generate_time || '06:00'}
                      onChange={(e) => handleChange('generate_time', e.target.value)}
                      className={`w-full px-3 py-2 rounded-lg border ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      } focus:outline-none focus:ring-2 focus:ring-blue-500/30`}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Timezone
                    </label>
                    <select
                      value={formData.timezone || 'Africa/Johannesburg'}
                      onChange={(e) => handleChange('timezone', e.target.value)}
                      className={`w-full px-3 py-2 rounded-lg border ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      } focus:outline-none focus:ring-2 focus:ring-blue-500/30`}
                    >
                      <option value="Africa/Johannesburg">South Africa (SAST)</option>
                      <option value="UTC">UTC</option>
                      <option value="Europe/London">London (GMT/BST)</option>
                      <option value="America/New_York">New York (EST/EDT)</option>
                    </select>
                  </div>
                </div>
                
                {/* Description */}
                <div>
                  <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Description / Notes
                  </label>
                  <textarea
                    value={formData.description || ''}
                    onChange={(e) => handleChange('description', e.target.value)}
                    rows={2}
                    placeholder="Internal notes about this recurring schedule..."
                    className={`w-full px-3 py-2 rounded-lg border resize-none ${
                      isDark 
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' 
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500/30`}
                  />
                </div>
              </div>
            )}
          </div>
          
          {/* Info Box */}
          <div className={`p-4 rounded-lg flex items-start gap-3 ${
            isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-50 text-blue-700'
          }`}>
            <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p>Documents will be generated automatically based on your schedule. You can pause or cancel the recurring schedule at any time.</p>
              {formData.next_generation_date && (
                <p className="mt-2 font-medium">
                  Next generation: {new Date(formData.next_generation_date).toLocaleDateString('en-ZA', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              )}
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className={`px-6 py-4 border-t flex items-center justify-between ${
          isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
        }`}>
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg ${
              isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'
            } transition-colors`}
          >
            Cancel
          </button>
          
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 font-medium transition-colors"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {existingSchedule?.id ? 'Update Schedule' : 'Create Recurring Schedule'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
